/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Icon } from "@material-ui/core";
import { FC } from "react";
import useGetFormattedDecimalValue from "src/hooks/table/cells/useGetFormattedDecimalValue/useGetFormattedDecimalValue";
import { TableColumns } from "src/requests/table/types";
import { ColumnFormatDict, ValueFormat } from "src/requests/templates/types";
import { FlowioInputType as InputType } from "../../../../globalTypes";
import ColorDot from "../ColorDot";
import FlowioColorInput, { FlowioColorInputProps } from "../ColorInput";
import FlowioDateInput, {
  FlowioDateInputProps,
} from "../FlowioDateInput/FlowioDateInput";
import useGetFormattedStringToDateValueGetter from "../FlowioDateInput/useGetFormattedStringToDateValue";
import { isStringUTCDate } from "../FlowioDateInput/utils";
import FlowioDateRange, { FlowioDateRangeProps } from "../FlowioDateRange";
import FlowioHiddenInput, {
  FlowioHiddenInputProps,
} from "../FlowioHiddenInput";
import FlowioIconInput, { FlowioIconInputProps } from "../FlowioIconInput";
import FlowioNumberInput, {
  FlowioNumberInputProps,
} from "../FlowioNumberInput/FlowioNumberInput";
import FlowioPasswordInput, {
  FlowioPasswordInputProps,
} from "../FlowioPasswordInput";
import FlowioSearchSelect, {
  FlowioSearchSelectProps,
} from "../FlowioSearchSelect/FlowioSearchSelect";
import FlowioSelect, { FlowioSelectProps } from "../FlowioSelect";
import FlowioMultiSelect, {
  FlowioMultiSelectProps,
} from "../FlowioMultiSelect";
import FlowioSwitch, { FlowioSwitchProps } from "../FlowioSwitch";
import FlowioTextarea, { FlowioTextareaProps } from "../FlowioTextarea";
import FlowioTextInput, { FlowioTextInputProps } from "../FlowioTextInput";
import {
  getFormattedAgrDateString,
  isAgrStringDate,
  isTextEmail,
  isTextLink,
  isTextPhone,
} from "./utils";

// meta = vztahující se k internímu fungování FlowioInput, ne k samotným inputům
type MetaProps = {
  noInputMode?: boolean;
  interpretPhoneAndEmail?: boolean;
  format?: ValueFormat;
  columns?: TableColumns;
  columnsFormat?: ColumnFormatDict;
  unformattedValue?: string;
};

// POZOR: Záleží na pořadí notace. Když bych zapisoval jako
// | ({ type: InputType.Select } & FlowioSelectProps)
// ... atd, tak to to rozbije užitečnost našeptávání
type Props = MetaProps &
  (
    | (FlowioSelectProps & { type: InputType.Select })
    | (FlowioMultiSelectProps & { type: InputType.MultiSelect })
    | (FlowioSearchSelectProps & { type: InputType.SelectWithSearch })
    | (FlowioNumberInputProps & { type: InputType.Number })
    | (FlowioDateInputProps & { type: InputType.Datetime })
    | (FlowioDateRangeProps & { type: InputType.DateRange })
    | (FlowioPasswordInputProps & { type: InputType.Password })
    | (FlowioSwitchProps & { type: InputType.Checkbox })
    | (FlowioTextareaProps & { type: InputType.Textarea })
    | (FlowioTextInputProps & { type: InputType.Text })
    | (FlowioIconInputProps & { type: InputType.Icon })
    | (FlowioColorInputProps & { type: InputType.Color })
    | (FlowioHiddenInputProps & { type: InputType.Hidden })
  );

const FlowioInput: FC<Props> = (props) => {
  const isControlled = props.value !== undefined;
  const { noInputMode } = props;
  const getFormattedDecimalValue = useGetFormattedDecimalValue();
  const getFormattedStringToDateValue =
    useGetFormattedStringToDateValueGetter();

  if (!noInputMode) {
    switch (props.type) {
      case InputType.Checkbox:
        return <FlowioSwitch {...props} />;
      case InputType.Textarea:
        return <FlowioTextarea {...props} />;
      case InputType.Select:
        return <FlowioSelect {...props} />;
      case InputType.MultiSelect:
        return <FlowioMultiSelect {...props} />;
      case InputType.SelectWithSearch:
        return <FlowioSearchSelect {...props} />;
      case InputType.Datetime:
        return <FlowioDateInput {...props} />;
      case InputType.DateRange:
        return <FlowioDateRange {...props} />;
      case InputType.Icon:
        return <FlowioIconInput {...props} />;
      case InputType.Hidden:
        return <FlowioHiddenInput {...props} />;
      case InputType.Number:
        return <FlowioNumberInput {...props} />;
      case InputType.Text:
        return <FlowioTextInput {...props} />;
      case InputType.Color:
        return <FlowioColorInput {...props} />;
      case InputType.Password:
        return <FlowioPasswordInput {...props} />;
      default:
    }
  }

  switch (props.type) {
    case InputType.Select:
    case InputType.SelectWithSearch: {
      const { options, value, defaultValue, className } = props;
      const active = options.find((o) => o.value === value) ?? null;
      const label = active?.name ?? value ?? defaultValue;
      return <span className={className}>{label}</span>;
    }
    case InputType.Datetime: {
      const { value, defaultValue, format } = props;
      const valueToUse = isControlled ? value : defaultValue;

      if (!valueToUse) return null;

      const dateString = getFormattedStringToDateValue(valueToUse, format);

      return <span>{dateString}</span>;
    }

    case InputType.Icon: {
      const { value, defaultValue } = props;
      const valueToDisplay = isControlled ? value : defaultValue;
      return <Icon className={props.className}>{valueToDisplay}</Icon>;
    }

    case InputType.Number: {
      const { value, defaultValue, className } = props;
      const valueToDisplay = isControlled ? value : defaultValue;
      const formattedValue = getFormattedDecimalValue(
        valueToDisplay,
        props.format
      );
      return <span className={className}>{formattedValue}</span>;
    }

    case InputType.Color: {
      return <ColorDot color={props.value} />;
    }

    case InputType.Password: {
      const { value, defaultValue, className } = props;
      const password = isControlled ? value : defaultValue;
      const passwordHidden = password?.replace(/./gi, "*");
      return <span className={className}>{passwordHidden}</span>;
    }

    case InputType.Textarea: {
      return <FlowioTextarea {...props} />;
    }

    case InputType.Text: {
      const {
        value,
        defaultValue,
        className,
        interpretPhoneAndEmail,
        format,
        columnsFormat,
      } = props;

      const displayValue = isControlled ? value : defaultValue;
      const text = String(displayValue) ?? "";

      // const displayDateValue = isStringUTCDate(unformattedValue);

      if (isAgrStringDate(text)) {
        const formattedText = getFormattedAgrDateString(
          text,
          columnsFormat,
          getFormattedStringToDateValue
        );
        return <span className={className}>{formattedText}</span>;
      }

      if (isStringUTCDate(text) && text) {
        const dateValueString = getFormattedStringToDateValue(text, format);
        return <span className={className}>{dateValueString}</span>;
      }

      const noref = { target: "_blank", rel: "nofollow noreferrer noopener" };
      const textPhone = isTextPhone(text);

      if (interpretPhoneAndEmail && textPhone) {
        const { number, formattedNumber } = textPhone;
        return (
          <a href={`tel:${number}`} {...noref}>
            {formattedNumber}
          </a>
        );
      }

      if (interpretPhoneAndEmail && isTextEmail(text.toLowerCase())) {
        return (
          <a href={`mailto:${text}`} {...noref}>
            {text}
          </a>
        );
      }

      if (isTextLink(String(text))) {
        return <a href={String(text)}>{text}</a>;
      }

      const formattedValue = getFormattedDecimalValue(displayValue, format);

      return <span className={className}>{formattedValue}</span>;
    }
    default:
      return null;
  }
};

export default FlowioInput;
export type FlowioInputProps = Props;
